// src/components/LandingPage.tsx
// דף הבית המעוצב של CARTAX - Mobile-first design

import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import AccessibilityWidget from "./shared/AccessibilityWidget";
import M1PetrolDieselCarIcon from "../assets/icons/m1-petrol-diesel-car.svg";
import { getCalculationCounter } from "../services/public/publicService";
import { mountGoogleTranslate } from "../utils/googleTranslate";

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(true);
  const [calculationCount, setCalculationCount] = useState<number>(6892);
  const [isCountLoading, setIsCountLoading] = useState<boolean>(true);
  // Google Translate Integration
  // בורר השפות. כל הלוגיקה מרוכזת ב-utils/googleTranslate כדי שהסקריפט
  // של גוגל ייטען פעם אחת בלבד בחיי הדף.
  useEffect(
    () =>
      mountGoogleTranslate("google_translate_element"),
    []
  );

  // Load calculation counter
  useEffect(() => {
    console.log("=== Counter useEffect STARTED ===");

    const fetchCounter = async () => {
      console.log("=== fetchCounter called ===");
      try {
        const response = await getCalculationCounter();
        console.log("=== Counter API response:", response);
        if (response.res === "OK") {
          setCalculationCount(response.total_count);
        }
      } catch (error) {
        console.error("Failed to load calculation counter:", error);
      } finally {
        setIsCountLoading(false);
      }
    };

    fetchCounter();
  }, []);

  // בדיקה אם המשתמש הפסיק אנימציות בנגישות
  useEffect(() => {
    const checkAnimationPreference = () => {
      const body = document.body;
      const hasNoAnimations = body.classList.contains(
        "accessibility-no-animations",
      );
      setShouldAnimate(!hasNoAnimations);
    };

    // בדיקה ראשונית
    checkAnimationPreference();

    // מאזין לשינויים בהעדפות נגישות
    const observer = new MutationObserver(checkAnimationPreference);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const scrollToContact = () => {
    const contactSection = document.getElementById("contact-section");
    if (contactSection) {
      contactSection.scrollIntoView({
        behavior: shouldAnimate ? "smooth" : "auto",
      });
    }
    setIsMenuOpen(false);
  };

  return (
    <div className="min-h-screen relative overflow-x-hidden" dir="rtl">
      {/* AccessibilityWidget */}
      <AccessibilityWidget />

      {/* רקע גרדיאנט + אנימציית גלים */}
      <div
        className="fixed inset-0 -z-10"
        style={{
          background:
            "linear-gradient(180deg, hsl(201, 60%, 85%), hsl(201, 40%, 96%))",
        }}
      >
        {/* אנימציית גלים - רק אם לא הופסקו אנימציות */}
        {shouldAnimate && (
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 120' preserveAspectRatio='none'%3E%3Cpath d='M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z' fill='%23003B7B'%3E%3C/path%3E%3C/svg%3E")`,
              backgroundRepeat: "repeat-x",
              backgroundSize: "1200px 120px",
              animation: "wave-animation 20s ease-in-out infinite",
            }}
          />
        )}
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-sm border-b border-blue-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* כפתור תפריט - שמאל */}
            <button
              onClick={toggleMenu}
              className="p-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="פתח תפריט"
              aria-expanded={isMenuOpen}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>

            {/* Google Translate - מרכז/שמאל */}
            <div className="flex-1 flex justify-center">
              <div
                id="google_translate_element"
                className="google-translate-landing"
              ></div>
            </div>

            {/* לוגו CARTAX - ימין */}
            <h1 className="text-xl font-bold text-blue-600">CarTax</h1>
          </div>
        </div>
      </header>

      {/* תפריט נפתח מימין */}
      <div
        className={`fixed top-0 right-0 h-full w-64 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full p-6">
          {/* כותרת + כפתור סגירה */}
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-lg font-semibold text-gray-800">תפריט</h2>
            <button
              onClick={toggleMenu}
              className="p-1 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="סגור תפריט"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* פריטי תפריט */}
          <nav className="flex-1 space-y-4">
            <Link
              to="/articles"
              onClick={toggleMenu}
              className="block text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-md transition-colors"
            >
              מאמרים
            </Link>
            <Link
              to="/about"
              onClick={toggleMenu}
              className="block text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-md transition-colors"
            >
              אודות
            </Link>
            {/* <Link
              to="/recommended-contacts"
              onClick={toggleMenu}
              className="block text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-md transition-colors"
            >
               אנשי קשר מומלצים
            </Link> */}
            <Link
              to="/terms-and-privacy"
              onClick={toggleMenu}
              className="block text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-md transition-colors"
            >
              תנאי שימוש ופרטיות
            </Link>
            <Link
              to="/accessibility-statement"
              onClick={toggleMenu}
              className="block text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-md transition-colors"
            >
              הצהרת נגישות
            </Link>
            <button
              onClick={scrollToContact}
              className="block w-full text-right text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-md transition-colors"
            >
              יצירת קשר
            </button>
            <Link
              to="/enduser/auth"
              onClick={toggleMenu}
              className="block text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-md transition-colors"
            >
              הרשמה
            </Link>
            <Link
              to="/enduser/auth"
              onClick={toggleMenu}
              className="block text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-md transition-colors"
            >
              התחברות
            </Link>
          </nav>
        </div>
      </div>

      {/* Overlay כהה כשהתפריט פתוח */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={toggleMenu}
          aria-hidden="true"
        />
      )}

      {/* תוכן ראשי */}
      <main className="max-w-7xl mx-auto px-4 pb-8">
        {/* Hero Section */}
        <section className="text-center py-12 md:py-16">
          <h1
            className="text-3xl md:text-5xl font-bold mb-4"
            style={{ color: "#003B7B" }}
          >
            ברוכים הבאים ל-CARTAX
          </h1>
          <h2
            className="text-2xl md:text-4xl font-bold mb-4"
            style={{ color: "#003B7B" }}
          >
            מחשבון יבוא רכב
          </h2>
          <h2 className="text-base md:text-xl text-gray-800 mb-3 max-w-3xl mx-auto leading-relaxed">
            הפלטפורמה היחידה בישראל לחישוב מיסים לכלי רכב ביבוא אישי ומסחרי
          </h2>
          <div
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-base md:text-lg font-medium shadow-lg"
            style={{ backgroundColor: "#003B7B", color: "white" }}
          >
            <span>
              במערכת CARTAX בוצעו עד כה{" "}
              <span className="font-bold text-xl">
                {isCountLoading
                  ? "..."
                  : calculationCount.toLocaleString("he-IL")}
              </span>{" "}
              חישובים
            </span>
          </div>
          <p className="text-sm md:text-base text-gray-700 mt-6 max-w-2xl mx-auto">
            CARTAX יודעת להגיד לך את המספרים האמיתיים – לפני שאתה מחליט לייבא
          </p>
        </section>

        {/* Video Section */}
        <section id="video-section" className="mt-12 max-w-4xl mx-auto">
          <h3
            className="text-xl md:text-2xl font-bold text-center mb-6"
            style={{ color: "#003B7B" }}
          >
            למדו איך זה עובד
          </h3>
          <div
            className="relative rounded-xl overflow-hidden shadow-2xl"
            style={{ paddingBottom: "56.25%" }}
          >
            <iframe
              className="absolute top-0 left-0 w-full h-full"
              src="https://www.youtube.com/embed/iTzjrudzJg8"
              title="CARTAX - למדו איך זה עובד"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </section>

        {/* מה זה CARTAX */}
        <section className="mt-16 max-w-5xl mx-auto">
          <h3
            className="text-2xl md:text-3xl font-bold text-center mb-8"
            style={{ color: "#003B7B" }}
          >
            מה זה CARTAX?
          </h3>
          <p className="text-center text-base md:text-lg text-gray-800 mb-8 max-w-4xl mx-auto leading-relaxed">
            CARTAX היא מערכת דיגיטלית הפועלת במקביל למערכת המכס, ומבצעת סימולציה
            מלאה ומדויקת של כל המיסים על כלי הרכב המיובאים לישראל – בהתאם
            להוראות חוק
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {/* כרטיס 1 */}
            <div className="bg-white/90 rounded-xl p-6 shadow-lg">
              <div className="grid grid-cols-[auto_1fr] gap-3 items-start mb-4">
                <svg
                  className="w-6 h-6 flex-shrink-0"
                  style={{ color: "#003B7B" }}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-gray-800 mr-3">
                  חישוב מכס, מס קניה, מע"מ, מס יוקרה, מחיר לצרכן, רמות זיהום
                  אוויר ואבזור בטיחותי
                </p>
              </div>
              <div className="flex items-start">
                <svg
                  className="w-6 h-6 mt-1 flex-shrink-0"
                  style={{ color: "#003B7B" }}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-gray-800 mr-3">
                  תמיכה ביבוא אישי וביבוא מסחרי
                </p>
              </div>
            </div>

            {/* כרטיס 2 */}
            <div className="bg-white/90 rounded-xl p-6 shadow-lg">
              <div className="flex items-start mb-4">
                <svg
                  className="w-6 h-6 mt-1 flex-shrink-0"
                  style={{ color: "#003B7B" }}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-gray-800 mr-3">
                  שמירת חישובים באזור אישי מאובטח
                </p>
              </div>
              <div className="flex items-start">
                <svg
                  className="w-6 h-6 mt-1 flex-shrink-0"
                  style={{ color: "#003B7B" }}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-gray-800 mr-3">
                  תצוגת נתונים נוחה ואינטראקטיבית
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* מה יוצא לך מזה */}
        <section className="mt-16 max-w-5xl mx-auto">
          <h3
            className="text-2xl md:text-3xl font-bold text-center mb-12"
            style={{ color: "#003B7B" }}
          >
            מה יוצא לך מזה?
          </h3>

          <div className="grid md:grid-cols-2 gap-8">
            {/* יבוא אישי */}
            <div className="bg-white/90 rounded-xl p-8 shadow-lg">
              <h4
                className="text-xl font-bold mb-6 flex items-center"
                style={{ color: "#003B7B" }}
              >
                <img
                  src={M1PetrolDieselCarIcon}
                  alt="רכב M1"
                  className="w-10 h-10 object-contain ml-3"
                />
                יבוא אישי
              </h4>
              <ul className="space-y-4">
                <li className="grid grid-cols-[auto_1fr] gap-3 items-start">
                  <svg
                    className="w-6 h-6 mt-1 flex-shrink-0"
                    style={{ color: "#003B7B" }}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-gray-800 mr-3">
                    לדעת מראש כמה באמת מיסים תצטרך לשלם כשהרכב יגיע
                  </span>
                </li>
                <li className="grid grid-cols-[auto_1fr] gap-3 items-start">
                  <svg
                    className="w-6 h-6 mt-1 flex-shrink-0"
                    style={{ color: "#003B7B" }}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-gray-800 mr-3">
                    להימנע מהפתעות יקרות אחרי שהזמנת רכב
                  </span>
                </li>
                <li className="grid grid-cols-[auto_1fr] gap-3 items-start">
                  <svg
                    className="w-6 h-6 mt-1 flex-shrink-0"
                    style={{ color: "#003B7B" }}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-gray-800 mr-3">
                    לקבל החלטה כלכלית הנכונה לפני רכישה מחו"ל
                  </span>
                </li>
                <li className="grid grid-cols-[auto_1fr] gap-3 items-start">
                  <svg
                    className="w-6 h-6 mt-1 flex-shrink-0"
                    style={{ color: "#003B7B" }}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-gray-800 mr-3">
                    זמינות מלאה 24/7/365
                  </span>
                </li>
                <li className="grid grid-cols-[auto_1fr] gap-3 items-start">
                  <svg
                    className="w-6 h-6 mt-1 flex-shrink-0"
                    style={{ color: "#003B7B" }}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-gray-800 mr-3">
                    שירות לקוחות אישי לכל שאלה
                  </span>
                </li>
              </ul>
            </div>

            {/* יבואנים מסחריים */}
            <div className="bg-white/90 rounded-xl p-8 shadow-lg">
              <h4
                className="text-xl font-bold mb-6 flex items-center"
                style={{ color: "#003B7B" }}
              >
                <span className="text-3xl ml-3">📋</span>
                יבואנים מסחריים
              </h4>
              <ul className="space-y-4">
                <li className="grid grid-cols-[auto_1fr] gap-3 items-start">
                  <svg
                    className="w-6 h-6 mt-1 flex-shrink-0"
                    style={{ color: "#003B7B" }}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-gray-800 mr-3">
                    חישובים מהירים ומדויקים לרוב כלי הרכב
                  </span>
                </li>
                <li className="grid grid-cols-[auto_1fr] gap-3 items-start">
                  <svg
                    className="w-6 h-6 mt-1 flex-shrink-0"
                    style={{ color: "#003B7B" }}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-gray-800 mr-3">כלי חישוב מקצועיים</span>
                </li>
                <li className="grid grid-cols-[auto_1fr] gap-3 items-start">
                  <svg
                    className="w-6 h-6 mt-1 flex-shrink-0"
                    style={{ color: "#003B7B" }}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-gray-800 mr-3">
                    אחידות בחישוב מול המכס
                  </span>
                </li>
                <li className="grid grid-cols-[auto_1fr] gap-3 items-start">
                  <svg
                    className="w-6 h-6 mt-1 flex-shrink-0"
                    style={{ color: "#003B7B" }}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-gray-800 mr-3">
                    שירות לקוחות אישי צמוד
                  </span>
                </li>
                <li className="grid grid-cols-[auto_1fr] gap-3 items-start">
                  <svg
                    className="w-6 h-6 mt-1 flex-shrink-0"
                    style={{ color: "#003B7B" }}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-gray-800 mr-3">
                    זמינות מלאה 24/7/365
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="mt-20 text-center max-w-3xl mx-auto">
          <h3
            className="text-2xl md:text-3xl font-bold mb-4"
            style={{ color: "#003B7B" }}
          >
            התחילו להשתמש ב-CARTAX כבר היום
          </h3>
          <p className="text-base md:text-lg text-gray-700 mb-8">
            גישה למערכת חישוב המיסים המדויקת בישראל – ביבוא אישי ומסחרי
          </p>
          <div className="flex flex-col md:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/enduser/auth")}
              className="text-white px-8 py-4 rounded-full text-base md:text-lg font-medium shadow-lg hover:opacity-90 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              style={{ backgroundColor: "#003B7B" }}
            >
              🔹 הרשמה למערכת
            </button>
            <button
              onClick={() => navigate("/enduser/auth")}
              className="bg-white px-8 py-4 rounded-full text-base md:text-lg font-medium shadow-lg hover:bg-gray-50 transition-all border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              style={{ color: "#003B7B", borderColor: "#003B7B" }}
            >
              ⚪ התחברת למנויים
            </button>
          </div>
        </section>
      </main>

      {/* Footer + Contact */}
      <footer
        id="contact-section"
        className="bg-white/90 backdrop-blur-sm border-t border-blue-100 mt-20 py-12"
      >
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h3 className="text-2xl font-bold mb-6" style={{ color: "#003B7B" }}>
            CARTAX
          </h3>

          <p className="text-gray-700 mb-2">
            © כל הזכויות שמורות – קאר טאקס בע"מ
          </p>
          <p className="text-gray-600 mb-6">
            מערכת חישוב מיסים לכלי רכב מיובאים
          </p>
          <p className="text-gray-500 text-sm mb-6">מעודכן נכון ל-01/01/2026</p>

          {/* מייל וטלפון */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-6">
            <a
              href="mailto:info@cartax.org"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
            >
              <svg
                className="w-5 h-5 ml-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <span className="font-medium">info@cartax.org</span>
            </a>

            <a
              href="https://wa.me/972544512136?text=%D7%A9%D7%9C%D7%95%D7%9D%20%D7%A8%D7%91%2C%20%D7%90%D7%A9%D7%9E%D7%97%20%D7%91%D7%9E%D7%99%D7%93%D7%A2%20%D7%A0%D7%95%D7%A1%D7%A3%20%D7%91%D7%A7%D7%A9%D7%A8%20%D7%9C%D7%9E%D7%A2%D7%A8%D7%9B%D7%AA%20Cartax"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-green-600 hover:text-green-800 transition-colors"
            >
              <svg
                className="w-5 h-5 ml-2"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              <span className="font-medium" dir="ltr">
                +972 54-451-2136
              </span>
            </a>
          </div>

          {/* קישור לתנאי שימוש */}
          <div className="mt-4">
            <Link
              to="/terms-and-privacy"
              className="text-sm text-blue-600 hover:text-blue-800 underline transition-colors"
            >
              תנאי שימוש ומדיניות פרטיות
            </Link>
          </div>

          {/* אלדור מערכות */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 mb-2">
              המערכת נבנתה ע"י אלדור מערכות
            </p>
            <div className="flex items-center justify-center gap-4">
              <a
                href="mailto:eldor4ziv@gmail.com"
                className="text-gray-500 hover:text-blue-600 transition-colors"
                title="שלח מייל לאלדור מערכות"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </a>
              <a
                href="https://wa.me/972546422629"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-green-600 transition-colors"
                title="שלח הודעת וואצאפ לאלדור מערכות"
              >
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* CSS לאנימציית הגלים + Google Translate styling */}
      <style>{`
        @keyframes wave-animation {
          0%, 100% {
            transform: translateX(0);
          }
          50% {
            transform: translateX(-50px);
          }
        }

        /* סגנונות בורר השפות עברו ל-utils/googleTranslate.ts */
      `}</style>
    </div>
  );
};

export default LandingPage;
